import { KeyboardEventHandler, PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react'
import Select, { ActionMeta, ClassNamesConfig, GroupBase, GroupHeadingProps, MultiValue, Props as ReactSelectProps, SelectComponentsConfig } from 'react-select'

import { BodyVariant, SizeOptions } from '../../../../types/types'
import { AstraParagraph } from '../../../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../../../Utilities/astraTwMerge'
import { SelectOption } from '../../AstraSelect'
import { sizeStyles } from '../../AstraSelect/utils'
import { MultiSelectOption } from '../types/types'
import { getGroupByLabel, getGroupCheckboxState, getGroupValues, isGroup } from '../utils/utils'
import {
    AstraMultiSelectClearIndicator,
    AstraMultiSelectGroupedMultiValue,
    AstraMultiSelectMenuList,
    AstraMultiSelectMultiValueContainer,
    AstraMultiSelectOption,
    AstraMultiSelectTextValueContainer,
} from './'
import { AstraMultiSelectDropdownIndicator } from './AstraMultiSelectDropdownIndicator'
import { AstraMultiSelectGroupHeading } from './AstraMultiSelectGroupHeading'

const SizeStylesParagraph: Record<SizeOptions, BodyVariant> = {
    small: 'body-small',
    medium: 'body',
    large: 'body',
}

type WithLabelProps = PropsWithChildren & {
    size: SizeOptions
    fullWidth?: boolean
    label?: string
    'data-test'?: string
}

const WithLabel = ({ children, fullWidth, label, size, 'data-test': dataTest }: WithLabelProps) => (
    <div className={astraTwMerge('flex flex-col gap-2', fullWidth ? 'w-full' : '')} data-test={dataTest}>
        {label && <AstraParagraph variant={SizeStylesParagraph[size]}>{label}</AstraParagraph>}
        {children}
    </div>
)

export type AstraMultiSelectCollapsableMode = 'expanded' | 'collapsed' | 'default'

/** How selected values are rendered in collapsed and inline modes: as removable chips or as truncated comma-separated text. */
export type AstraMultiSelectCollapsedDisplay = 'chips' | 'text'

export type AstraMultiSelectCollapsableProps<T> = Omit<
    ReactSelectProps<SelectOption<T>, true, GroupBase<SelectOption<T>>>,
    'size' | 'options' | 'value' | 'onChange'
> & {
    mode: AstraMultiSelectCollapsableMode
    displaySelectedAs?: AstraMultiSelectCollapsedDisplay
    options: MultiSelectOption<T>[]
    selectedOptions: SelectOption<T>[]
    filteredOptions: MultiSelectOption<T>[]
    groups: Array<{ label: string; options: SelectOption<T>[] }>
    openGroups: Record<string, boolean>
    groupedOptionValues: Set<T>
    fullySelectedGroups: Set<string>
    valueToGroupLabel: Map<T, string>
    onOpenGroupsChange: (groups: Record<string, boolean>) => void
    size?: SizeOptions
    dropdownIcon?: ReactNode
    /** Optional heading rendered at the top of the options menu. */
    menuTitle?: ReactNode
    expandedGroups?: boolean
    disableGroupSelection?: boolean
    label?: string
    readOnly?: boolean
    fullWidth?: boolean
    onChange?: (values: SelectOption<T>[]) => void
    onActionMeta?: (actionMeta: ActionMeta<SelectOption<T>>) => void
    /** Notifies when the inline-mode menu opens or closes. */
    onMenuOpenChange?: (open: boolean) => void
    'data-test'?: string
}

export function AstraMultiSelectCollapsable<T>({
    mode,
    displaySelectedAs = 'chips',
    options,
    selectedOptions,
    filteredOptions,
    groups = [],
    openGroups,
    groupedOptionValues,
    fullySelectedGroups,
    valueToGroupLabel,
    onOpenGroupsChange,
    size = 'small',
    dropdownIcon,
    menuTitle,
    expandedGroups = false,
    disableGroupSelection,
    label,
    placeholder,
    isDisabled,
    readOnly,
    fullWidth,
    onChange,
    onActionMeta,
    onMenuOpenChange,
    'data-test': dataTest,
    ...rest
}: AstraMultiSelectCollapsableProps<T>) {
    const inlineRef = useRef<HTMLDivElement>(null)
    const [inlineMenuOpen, setInlineMenuOpen] = useState(false)
    const isInlineMode = mode === 'default'

    useEffect(() => {
        if (!isInlineMode) return
        onMenuOpenChange?.(inlineMenuOpen)
    }, [isInlineMode, inlineMenuOpen, onMenuOpenChange])

    // Inline mode renders its menu in a portal and has no visible search input to blur, so close on outside click ourselves.
    useEffect(() => {
        if (!isInlineMode || !inlineMenuOpen) return
        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node
            if (inlineRef.current?.contains(target)) return
            if (document.querySelector('.di-multiselect__menu')?.contains(target)) return
            setInlineMenuOpen(false)
        }
        document.addEventListener('mousedown', handlePointerDown)
        return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [isInlineMode, inlineMenuOpen])

    const isOptionGrouped = (option: SelectOption<T>) => groupedOptionValues.has(option.value)

    const shouldIndentOption = (option: SelectOption<T>) => !disableGroupSelection && isOptionGrouped(option)

    // Handlers for group checkbox click (select/unselect all)
    const handleGroupSelected = (header: GroupBase<SelectOption<T>>, checked: boolean) => {
        if (!header.label) return
        const group = getGroupByLabel(options, header.label)
        if (!group) return
        const groupValues = getGroupValues(group)
        if (checked) {
            // Add all group options that are not already selected
            const toAdd = group.options.filter(option => !selectedOptions.some(selected => selected.value === option.value))
            onChange?.([...selectedOptions, ...toAdd])
        } else {
            // Remove all group options
            onChange?.(selectedOptions.filter(option => !groupValues.includes(option.value)))
        }
    }

    const handleExpandGroup = (group: GroupBase<SelectOption<T>>, isExpand: boolean) => {
        const { label } = group
        if (label === undefined) return
        onOpenGroupsChange({ ...openGroups, [label]: isExpand })
    }

    const expandAllGroups: KeyboardEventHandler<HTMLDivElement | HTMLSelectElement> = event => {
        event.stopPropagation()
        const isAlphanumericKeyRegex = /[\p{L}\p{N}]/u
        if (event.key && isAlphanumericKeyRegex.test(event.key) && event.key.length === 1) {
            const all = options.reduce<Record<string, boolean>>((acc, option) => {
                if (isGroup(option)) acc[option.label] = true
                return acc
            }, {})
            onOpenGroupsChange(all)
        }
    }

    const GroupHeadingComponent = (headingProps: GroupHeadingProps<SelectOption<T>, true, GroupBase<SelectOption<T>>>) => {
        const groupLabel = headingProps.data.label

        // If group option is not selectable, render as label
        if (disableGroupSelection) {
            return (
                <div className="pointer-events-none py-1">
                    <AstraParagraph variant="body-small" weight="medium" className="p-2 text-subtle">
                        {groupLabel}
                    </AstraParagraph>
                </div>
            )
        }

        const optionGroup = groupLabel ? getGroupByLabel(options, groupLabel) : undefined
        if (!optionGroup) return null

        const checked = getGroupCheckboxState(
            optionGroup,
            selectedOptions.map(option => option.value)
        )
        const isExpanded = openGroups[optionGroup.label] ?? expandedGroups

        return (
            <AstraMultiSelectGroupHeading
                {...headingProps}
                size={size}
                isExpanded={isExpanded}
                onGroupClick={handleGroupSelected}
                onGroupExpandClick={handleExpandGroup}
                checked={checked}
            />
        )
    }

    const collapsedMode = mode === 'collapsed'
    const defaultMode = mode === 'default'
    const collapsedTextMode = collapsedMode && displaySelectedAs === 'text'
    const defaultTextMode = defaultMode && displaySelectedAs === 'text'
    const textMode = collapsedTextMode || defaultTextMode

    let valueContainerOverride: Partial<SelectComponentsConfig<SelectOption<T>, true, GroupBase<SelectOption<T>>>> = {}
    if (textMode) {
        valueContainerOverride = {
            ValueContainer: _props => (
                <AstraMultiSelectTextValueContainer
                    {..._props}
                    selectedOptions={selectedOptions}
                    placeholder={placeholder}
                    size={size}
                    keepFocusTarget={defaultTextMode}
                />
            ),
        }
    } else if (collapsedMode) {
        valueContainerOverride = { ValueContainer: _props => <AstraMultiSelectMultiValueContainer {..._props} /> }
    }

    const components: SelectComponentsConfig<SelectOption<T>, true, GroupBase<SelectOption<T>>> = {
        MultiValue: defaultTextMode
            ? () => null
            : _props => (
                  <AstraMultiSelectGroupedMultiValue
                      {..._props}
                      size={size}
                      groups={groups}
                      fullySelectedGroups={fullySelectedGroups}
                      valueToGroupLabel={valueToGroupLabel}
                      selectedOptions={selectedOptions}
                      onChange={onChange}
                      isDisabled={isDisabled}
                      readOnly={readOnly}
                  />
              ),
        GroupHeading: GroupHeadingComponent,
        ClearIndicator: _props => <AstraMultiSelectClearIndicator {..._props} size={size} />,
        DropdownIndicator: _props => (
            <AstraMultiSelectDropdownIndicator {..._props} icon={dropdownIcon} size={size} isDisabled={Boolean(isDisabled || readOnly)} />
        ),
        IndicatorSeparator: () => null,
        ...valueContainerOverride,
        // Inline text mode renders its own placeholder; suppress react-select's to avoid duplicates.
        ...(defaultTextMode && { Placeholder: () => null }),
        ...(collapsedMode && { Input: () => null }),
        ...(!collapsedMode && {
            Option: _props => <AstraMultiSelectOption {..._props} size={size} indent={shouldIndentOption(_props.data)} />,
        }),
        ...(menuTitle !== undefined && {
            MenuList: _props => <AstraMultiSelectMenuList {..._props} title={menuTitle} size={size} />,
        }),
    }

    const classNames: ClassNamesConfig<SelectOption<T>, true, GroupBase<SelectOption<T>>> = {
        control: () =>
            astraTwMerge(
                `flex max-h-64 w-full gap-y-1 overflow-y-auto !rounded-md py-0 pl-2 !shadow-none [&:not(:active)]:border-strong [&:not(:active)]:bg-surface-base`,
                sizeStyles[size],
                collapsedMode || textMode ? '!flex-nowrap' : 'flex-wrap',
                isDisabled ? '[&:not(:active)]:border-disabled [&:not(:active)]:bg-surface-disabled' : '',
                readOnly ? '[&:not(:active)]:border-base [&:not(:active)]:bg-surface-muted' : ''
            ),
        valueContainer: () => `flex items-center gap-1 !pl-0 flex-1 min-w-0 !py-0 ${collapsedMode || textMode ? '!flex-nowrap' : 'flex-wrap'}`,
        indicatorsContainer: () => 'shrink-0 flex [&>div:first-child:not(:only-child)]:p-0',
        input: () => '!m-0 !p-0 max-w-32',
        dropdownIndicator: () => '!p-0 cursor-pointer self-start shrink-0 top-0 sticky',
        clearIndicator: () => 'pl-1 cursor-pointer self-start shrink-0 top-0 sticky',
        menuList: () => 'w-full min-w-max',
        menu: () => 'bg-surface-base w-auto min-w-fit',
        multiValue: () => 'max-w-full',
        multiValueRemove: () => 'cursor-pointer',
        group: () => '!p-0',
        menuPortal: () => 'z-priority pointer-events-auto',
    }

    const handleOnChange = (opts: MultiValue<SelectOption<T>>, actionMeta: ActionMeta<SelectOption<T>>) => {
        onActionMeta?.(actionMeta)
        if (Array.isArray(opts)) onChange?.(opts as SelectOption<T>[])
        else onChange?.([])
    }

    return (
        <WithLabel fullWidth={fullWidth} size={size} label={label} data-test={`${dataTest}-multiselect`}>
            <div ref={inlineRef}>
                <Select
                    isMulti
                    options={filteredOptions}
                    value={selectedOptions}
                    onChange={handleOnChange}
                    placeholder={placeholder}
                    isDisabled={isDisabled || readOnly}
                    hideSelectedOptions={false}
                    classNamePrefix="di-multiselect"
                    components={components}
                    classNames={classNames}
                    onKeyDown={expandAllGroups}
                    // Full mode extras
                    {...(!collapsedMode && {
                        noOptionsMessage: () => 'Ingen alternativer',
                        closeMenuOnSelect: false,
                        blurInputOnSelect: false,
                    })}
                    {...(!defaultMode && { menuIsOpen: !collapsedMode })}
                    {...(defaultMode && {
                        menuPortalTarget: typeof window !== 'undefined' ? document.body : null,
                        menuIsOpen: inlineMenuOpen,
                        onMenuOpen: () => setInlineMenuOpen(true),
                        onMenuClose: () => setInlineMenuOpen(false),
                    })}
                    {...(defaultTextMode && { isSearchable: false })}
                    {...rest}
                />
            </div>
        </WithLabel>
    )
}
